import { useNavigate } from 'react-router-dom';
import { useUiStore, type ViewStateFilters, type ViewStateKey } from '../../store/ui';

export interface BusinessOsNavigationTarget {
  route: string;
  view: ViewStateKey;
  filters?: ViewStateFilters;
  year?: number;
}

function cleanText(value: unknown): string {
  return String(value || '').trim();
}

function joinSearch(parts: Array<unknown>): string {
  return parts
    .map(cleanText)
    .filter(Boolean)
    .join(' ');
}

export function toPipelineTarget(input: {
  search?: string;
  status?: string;
  flag?: string;
  entityId?: string;
}): BusinessOsNavigationTarget {
  return {
    route: '/pipeline',
    view: 'pipeline',
    filters: {
      search: cleanText(input.search),
      status: cleanText(input.status),
      flag: cleanText(input.flag),
      entityId: cleanText(input.entityId),
    },
  };
}

export function toMissionsTarget(input: {
  search?: string;
  category?: string;
  scope?: string;
  flag?: string;
  entityId?: string;
}): BusinessOsNavigationTarget {
  return {
    route: '/missions',
    view: 'missions',
    filters: {
      search: cleanText(input.search),
      category: cleanText(input.category),
      scope: cleanText(input.scope),
      flag: cleanText(input.flag),
      entityId: cleanText(input.entityId),
    },
  };
}

export function toAffectationTarget(input: {
  search?: string;
  status?: string;
  monthKey?: string;
}): BusinessOsNavigationTarget {
  return {
    route: '/affectation',
    view: 'affectation',
    filters: {
      search: cleanText(input.search),
      status: cleanText(input.status),
      monthKey: cleanText(input.monthKey),
    },
  };
}

export function toCalendrierTarget(input: {
  resourceId?: string;
  monthKey?: string;
  entityId?: string;
  createEvent?: string;
}): BusinessOsNavigationTarget {
  return {
    route: '/calendrier',
    view: 'calendrier',
    filters: {
      resourceId: cleanText(input.resourceId),
      monthKey: cleanText(input.monthKey),
      entityId: cleanText(input.entityId),
      createEvent: cleanText(input.createEvent),
    },
  };
}

export function toClientsTarget(input: {
  search?: string;
  status?: string;
  entityId?: string;
}): BusinessOsNavigationTarget {
  return {
    route: '/clients',
    view: 'clients',
    filters: {
      search: cleanText(input.search),
      status: cleanText(input.status),
      entityId: cleanText(input.entityId),
    },
  };
}

export function toIntervenantsTarget(input: {
  search?: string;
  status?: string;
  entityId?: string;
  monthKey?: string;
  demandDays?: string;
  flag?: string;
}): BusinessOsNavigationTarget {
  return {
    route: '/intervenants',
    view: 'intervenants',
    filters: {
      search: cleanText(input.search),
      status: cleanText(input.status),
      entityId: cleanText(input.entityId),
      monthKey: cleanText(input.monthKey),
      demandDays: cleanText(input.demandDays),
      flag: cleanText(input.flag),
    },
  };
}

export function toFacturesTarget(input: {
  search?: string;
  status?: string;
  flag?: string;
  scope?: 'billing' | 'collection' | 'cash';
  monthKey?: string;
  entityId?: string;
  year?: number;
}): BusinessOsNavigationTarget {
  return {
    route: '/factures',
    view: 'factures',
    year: input.year,
    filters: {
      search: cleanText(input.search),
      status: cleanText(input.status),
      flag: cleanText(input.flag),
      entityId: cleanText(input.entityId),
      ...(input.scope ? { scope: cleanText(input.scope) } : {}),
      ...(input.monthKey ? { monthKey: cleanText(input.monthKey) } : {}),
    },
  };
}

export function toActionsTarget(input: {
  search?: string;
  status?: string;
  flag?: string;
} = {}): BusinessOsNavigationTarget {
  return {
    route: '/ma-semaine',
    view: 'actions',
    filters: {
      search: cleanText(input.search),
      status: cleanText(input.status),
      flag: cleanText(input.flag),
    },
  };
}

export function toFinanceOpsTarget(input: {
  flag?: string;
  entityId?: string;
  severity?: string;
} = {}): BusinessOsNavigationTarget {
  return {
    route: '/finance-ops',
    view: 'finance_ops',
    filters: {
      flag: cleanText(input.flag),
      entityId: cleanText(input.entityId),
      severity: cleanText(input.severity),
    },
  };
}

export function toDataQualityTarget(input: {
  severity?: string;
  entityType?: string;
} = {}): BusinessOsNavigationTarget {
  return {
    route: '/data-quality',
    view: 'data_quality',
    filters: {
      severity: cleanText(input.severity),
      entityType: cleanText(input.entityType),
    },
  };
}

/**
 * « Ouvrir le connecteur X » opens THAT connector in the docked panel
 * (`/integrations?connecteur=<clé>`, DR-4); only the identity-link panel
 * (search / linkType) and a severity narrowing stay list filters.
 */
function connectorOnly(filters: Record<string, unknown> | undefined): string {
  const connector = cleanText(filters?.connector as string | undefined);
  if (!connector || connector === 'all' || connector.includes(',')) return '';
  if (cleanText(filters?.entityType as string | undefined) || cleanText(filters?.search as string | undefined)) return '';
  if (cleanText(filters?.severity as string | undefined)) return '';
  return connector.toLowerCase() === 'quonto' ? 'qonto' : connector.toLowerCase();
}

function connectorRoute(connector: string): BusinessOsNavigationTarget {
  return { route: `/integrations?connecteur=${encodeURIComponent(connector)}`, view: 'integrations', filters: {} };
}

export function toIntegrationsTarget(input: {
  connector?: string;
  severity?: string;
  search?: string;
  linkType?: 'client' | 'company' | 'client_invoice';
} = {}): BusinessOsNavigationTarget {
  const filters = {
    connector: cleanText(input.connector),
    severity: cleanText(input.severity),
    // search + entityType ouvrent le panneau « Rapprochements d'identités »
    // pré-filtré (entityType porte le type de lien : client, company,
    // client_invoice).
    search: cleanText(input.search),
    entityType: cleanText(input.linkType),
  };
  const connector = connectorOnly(filters);
  if (connector) return connectorRoute(connector);
  return {
    route: '/integrations',
    view: 'integrations',
    filters,
  };
}

export function toClient360Target(input: {
  entityId?: string;
  search?: string;
  segment?: string;
} = {}): BusinessOsNavigationTarget {
  return {
    route: '/client-360',
    view: 'client360',
    filters: {
      entityId: cleanText(input.entityId),
      search: cleanText(input.search),
      segment: cleanText(input.segment),
    },
  };
}

export function toAiInsightsTarget(input: {
  severity?: string;
  flag?: string;
} = {}): BusinessOsNavigationTarget {
  return {
    route: '/ai',
    view: 'ai_insights',
    filters: {
      severity: cleanText(input.severity),
      flag: cleanText(input.flag),
    },
  };
}

export function useBusinessOsNavigation() {
  const navigate = useNavigate();
  const replaceViewState = useUiStore((state) => state.replaceViewState);
  const setSelectedYear = useUiStore((state) => state.setSelectedYear);

  return (target: BusinessOsNavigationTarget) => {
    if (target.year && Number.isInteger(target.year) && target.year >= 2000 && target.year <= new Date().getFullYear() + 2) {
      setSelectedYear(target.year);
    }
    // Server-built targets (my-week, AI actions) still carry the connector as
    // a filter: route them to the connector itself, like toIntegrationsTarget.
    const connector = target.route === '/integrations' ? connectorOnly(target.filters as Record<string, unknown>) : '';
    const effective = connector ? connectorRoute(connector) : target;
    replaceViewState(effective.view, effective.filters || {});
    navigate(effective.route);
  };
}

export const navigationSearch = {
  fromClient: (clientName: unknown) => joinSearch([clientName]),
  fromMission: (missionName: unknown) => joinSearch([missionName]),
  fromFacture: (factureName: unknown, numeroFacture: unknown) => joinSearch([factureName, numeroFacture]),
  fromIntervenant: (resourceName: unknown) => joinSearch([resourceName]),
};
